import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useResizeObserver } from 'shared/hooks/use-resize-observer';

import * as Styled from './slider-buttons.style';

export type Option<Value extends string> =
  | {
      icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
      value: Value;
      tooltip?: string;
    }
  | {
      label: string;
      value: Value;
      tooltip?: string;
    };

type ButtonProps = {
  label?: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  value: string;
  tooltip?: string;
  selected: boolean;
  onClick: (value: string) => void;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ label, icon, value, tooltip, selected, onClick }, ref) => {
    const _ref = useForwardedRef(ref);

    const _onClick = () => onClick(value);

    return (
      <>
        <Styled.Button
          ref={_ref}
          onClick={_onClick}
          $selected={selected}
          type="button"
        >
          {icon ? (
            <Styled.IconContainer>
              <Icon icon={icon} />
            </Styled.IconContainer>
          ) : (
            label
          )}
        </Styled.Button>
        {!!tooltip && <Tooltip label={tooltip} referenceElement={_ref} />}
      </>
    );
  },
);

export type SliderButtonsProps<Value extends string> = {
  optionGroups: Option<Value>[][];
  value?: Value;
  onOption: (value: Value) => void;
  isFullWidth?: boolean;
  preventDrag?: boolean;
  hasDarkBackground?: boolean;
};

export const SliderButtons = <Value extends string>({
  optionGroups,
  value,
  onOption,
  isFullWidth,
  preventDrag,
  hasDarkBackground,
}: SliderButtonsProps<Value>): ReturnType<React.FC> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const allOptions = useMemo(() => optionGroups.flat(1), [optionGroups]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [selectedDimensions, setSelectedDimensions] = useState<{
    width: number;
    offsetLeft: number;
  } | null>(null);

  const updateDimensions = useCallback(() => {
    const selectedIndex = allOptions.findIndex(
      (option) => option.value === value,
    );
    if (selectedIndex !== -1 && buttonRefs.current[selectedIndex]) {
      const { offsetWidth: width, offsetLeft } =
        buttonRefs.current[selectedIndex]!;
      setSelectedDimensions({ width, offsetLeft });
    }
  }, [allOptions, value]);

  useResizeObserver(containerRef, updateDimensions, 10);

  useEffect(() => {
    updateDimensions();
  }, [updateDimensions]);

  return (
    <Styled.Container
      ref={containerRef}
      $selectedWidth={selectedDimensions?.width}
      $selectedOffset={selectedDimensions?.offsetLeft}
      $isFullWidth={!!isFullWidth}
      $preventDrag={!!preventDrag}
      $hasDarkBackground={!!hasDarkBackground}
    >
      {optionGroups.map((options, index) => (
        <React.Fragment key={`group-${index}`}>
          {options.map((option) => {
            const optionIndex = allOptions.findIndex(
              ({ value }) => value === option.value,
            );
            const buttonRef = (el: HTMLButtonElement | null) =>
              (buttonRefs.current[optionIndex] = el);

            const _onOption = () => onOption(option.value);

            return (
              <Button
                key={option.value}
                label={'label' in option ? option.label : undefined}
                icon={'icon' in option ? option.icon : undefined}
                value={option.value}
                tooltip={option.tooltip}
                selected={option.value === value}
                onClick={_onOption}
                ref={buttonRef}
              />
            );
          })}
          {index !== optionGroups.length - 1 && <Styled.Divider />}
        </React.Fragment>
      ))}
    </Styled.Container>
  );
};
